import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import {
  defineMessages,
  FormattedMessage,
  injectIntl,
  intlShape,
} from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';
import { Alert } from 'reactstrap';

import globalTranslations from '../../../i18n/globalTranslations';
import { modelOf } from '../../../prop-types';
import RouteService from '../../../services/RouteService';
import AccountStore from '../../../store/AccountStore';
import CustomerUserStore from '../../../store/CustomerUserStore';
import ValidationStatus, {
  errorMap,
} from '../../../types/form/ValidationStatus';
import Paths from '../../../types/Paths';
import RequestState from '../../../types/RequestState';
import { createErrorModel } from '../../../util/error';
import generatePath from '../../../util/generatePath';
import HelperNavigation from '../../common/HelperNavigation';
import PageTitle from '../../common/PageTitle';
import SEOTitle from '../../head/SEOTitle';
import ContentForState from '../../loader/ContentForState';
import ErrorHandler from '../../loader/ErrorHandler';
import BalanceUserForm from '../../user/BalanceUserForm';
import UserForm from '../../user/UserForm';

const messages = defineMessages({
  updateUser: {
    id: 'myAccount.updateUser',
    defaultMessage: 'Update User',
  },
});

const MyAccountUser = ({
  accountStore,
  customerUserStore,
  intl,
  routeService,
}) => {
  const { id } = useParams();
  const history = useHistory();
  const [userAccountState, setUserAccountState] = useState({
    error: null,
    status: ValidationStatus.NONE,
    requestState: RequestState.NONE,
    // This is used to know, if we've made a new request so we can re-animate errors
    requestId: 0,
  });

  useEffect(() => {
    loadUserAccountType();
  }, [id]);

  const loadUserAccountType = () => {
    if (accountStore.isBalanceBuyer) {
      maybeLoadBalanceUser();
    } else {
      maybeLoadUser();
    }
  };

  const maybeLoadUser = () => {
    const user = getActiveUser();
    if (!user) {
      customerUserStore.loadUser(id).catch((e) => console.error(e));
    }
  };

  const maybeLoadBalanceUser = () => {
    const user = getActiveUser();
    if (!user) {
      customerUserStore.loadBalanceUser(id).catch((e) => console.error(e));
    }
  };

  const getBreadcrumbsPath = () => {
    return routeService.transformBreadCrumbs([
      {
        text: intl.formatMessage(globalTranslations.myAccount),
        url: Paths.MyAccount,
      },
      {
        text: intl.formatMessage(globalTranslations.userListTitle),
        url: Paths.MyAccountUserList,
      },
      {
        text: intl.formatMessage(messages.updateUser),
        url: generatePath(Paths.MyAccountUser, {
          id,
        }),
      },
    ]);
  };

  const getActiveUser = () => {
    return accountStore.isBalanceBuyer
      ? customerUserStore.balanceUsers.get(id)
      : customerUserStore.users.get(id);
  };

  const updateCustomerUser = (user) => {
    user.isActive = !!user.isActive;
    user.notifyUser = !!user.notifyUser;
    user.allowUserCreateAddress = !!user.allowUserCreateAddress;
    user.allowUserMakePurchase = !!user.allowUserMakePurchase;

    setUserAccountState({
      ...userAccountState,
      requestState: RequestState.LOADING,
    });

    customerUserStore
      .updateUser(id, user)
      .then(() => {
        setUserAccountState({
          ...userAccountState,
          requestState: RequestState.LOADED,
          requestId: userAccountState.requestId + 1,
          error: null,
        });

        customerUserStore.loadUsers();
        navigateToUserList();
      })
      .catch((error) => {
        const status = error.response ? error.response.status : 500;

        setUserAccountState({
          ...userAccountState,
          requestState: RequestState.ERROR,
          status: errorMap[status],
          error,
          requestId: userAccountState.requestId + 1,
        });
      });
  };

  const navigateToUserList = () => {
    history.push(routeService.getPath(Paths.MyAccountUserList));
  };

  const updateBalanceUser = (user) => {
    user.isActive = !!user.isActive;
    user.notifyUser = !!user.notifyUser;

    setUserAccountState({
      ...userAccountState,
      requestState: RequestState.LOADING,
    });

    customerUserStore
      .updateBalanceUser(id, user)
      .then(() => {
        setUserAccountState({
          ...userAccountState,
          status: ValidationStatus.SUCCESS,
          requestState: RequestState.LOADED,
          requestId: userAccountState.requestId + 1,
          error: null,
        });
        customerUserStore.loadBalanceUsers();
        navigateToBalanceUserList();
      })
      .catch((error) => {
        const status = error.response ? error.response.status : 500;

        setUserAccountState({
          ...userAccountState,
          requestState: RequestState.ERROR,
          status: errorMap[status],
          error,
          requestId: userAccountState.requestId + 1,
        });
      });
  };

  const navigateToBalanceUserList = () => {
    history.push(routeService.getPath(Paths.MyAccountBalanceUser));
  };

  const renderUserForm = () => {
    return accountStore.isBalanceBuyer ? (
      <BalanceUserForm
        user={getActiveUser()}
        onSubmit={updateBalanceUser}
        buttonText={<FormattedMessage {...globalTranslations.update} />}
      />
    ) : (
      <UserForm
        loading={userAccountState === RequestState.LOADING}
        user={getActiveUser()}
        onSubmit={updateCustomerUser}
        buttonText={<FormattedMessage {...globalTranslations.update} />}
      />
    );
  };

  const getErrorMessages = () => {
    const errors = Object.values(userAccountState.error.response.data.errors);
    return errors.length > 0 && errors;
  };

  const renderError = () => {
    const { error, requestId, status } = userAccountState;
    const errors = getErrorMessages();
    let message = null;

    switch (status) {
      case ValidationStatus.CONFLICT:
      case ValidationStatus.VALIDATION_ERROR:
        message =
          (errors && (
            <Alert
              className="MyAccountUser__errors"
              color="danger"
              key={requestId}
            >
              {errors.flat().map((message) => (
                <div key={message}>{message}</div>
              ))}
            </Alert>
          )) ||
          null;
        break;
      case ValidationStatus.UNEXPECTED_ERROR:
        message =
          (error && <ErrorHandler error={createErrorModel(error)} />) || null;
        break;
      case ValidationStatus.NONE:
      default:
        message = null;
        break;
    }

    return message;
  };

  const { error } = userAccountState;
  const accountTypeState = accountStore.isBalanceBuyer
    ? customerUserStore.balanceUserStates.get(id)
    : customerUserStore.userStates.get(id);

  return (
    <div className="MyAccountUser">
      <SEOTitle title={messages.updateUser} />
      <HelperNavigation breadcrumbsPath={getBreadcrumbsPath()} />
      <PageTitle>
        <FormattedMessage {...messages.updateUser} />
      </PageTitle>
      {error && renderError()}
      <ContentForState
        state={accountTypeState}
        error={customerUserStore.lastError}
        forLoaded={() => renderUserForm()}
      />
    </div>
  );
};

MyAccountUser.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  customerUserStore: modelOf(CustomerUserStore).isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
  intl: intlShape.isRequired,
};

export default injectIntl(
  inject(
    'accountStore',
    'customerUserStore',
    'routeService'
  )(observer(MyAccountUser))
);
